import { UserRole } from './auth'

export interface ImpersonatedUser {
  id: string
  clerkUserId: string
  name: string
  email?: string
  role: UserRole
  companyName?: string // For employers
  headline?: string // For job seekers
}

export interface ImpersonationSession {
  adminId: string
  impersonatedUser: ImpersonatedUser
  startedAt: Date
  lastActivity: Date
}

// Session storage keys - CRITICAL: Make unique per admin to prevent cross-contamination
const getImpersonationSessionKey = (adminId?: string) => {
  if (adminId) {
    return `admin_impersonation_session_${adminId}`
  }
  // Fallback to generic key for backward compatibility, but this is dangerous
  return 'admin_impersonation_session'
}
const SESSION_TIMEOUT = 2 * 60 * 60 * 1000 // 2 hours in milliseconds

/**
 * Get current impersonation session from session storage
 */
export function getImpersonationSession(adminId?: string): ImpersonationSession | null {
  if (typeof window === 'undefined') return null

  try {
    let sessionData = null
    let sessionKey = ''

    if (adminId) {
      sessionKey = getImpersonationSessionKey(adminId)
      sessionData = sessionStorage.getItem(sessionKey)
    } else {
      // No adminId provided — search for any admin-specific session.
      // Security note: in multi-admin tab scenarios this could return the wrong session.
      // Prefer getSecureImpersonationSession(adminId) when the admin ID is available.
      const allKeys = Object.keys(sessionStorage)
      const adminSessionKeys = allKeys.filter(key => key.startsWith('admin_impersonation_session_') && key !== 'admin_impersonation_session')

      if (adminSessionKeys.length > 1) {
        console.warn('🚨 IMPERSONATION: Multiple admin sessions detected — using first found:', adminSessionKeys)
      }

      if (adminSessionKeys.length > 0) {
        sessionKey = adminSessionKeys[0]
        sessionData = sessionStorage.getItem(sessionKey)
      }
    }

    // Fallback to generic key if no admin-specific session found
    if (!sessionData) {
      sessionKey = getImpersonationSessionKey()
      sessionData = sessionStorage.getItem(sessionKey)
    }

    if (!sessionData) return null

    const session: ImpersonationSession = JSON.parse(sessionData)

    // Check if session has expired
    const now = new Date()
    const lastActivity = new Date(session.lastActivity)
    const timeDiff = now.getTime() - lastActivity.getTime()

    if (timeDiff > SESSION_TIMEOUT) {
      console.log('🎭 IMPERSONATION: Session expired, clearing')
      clearImpersonationSession()
      return null
    }

    // Update last activity using admin-specific key
    session.lastActivity = now
    const updateKey = getImpersonationSessionKey(session.adminId)
    sessionStorage.setItem(updateKey, JSON.stringify(session))

    return session
  } catch (error) {
    console.error('🎭 IMPERSONATION ERROR: Failed to get session:', error)
    clearImpersonationSession()
    return null
  }
}

/**
 * SECURE: Get impersonation session for the current admin user
 * This function safely retrieves the session using the current admin's ID
 * Note: This should be called from within a React component that has access to the current user
 */
export async function getCurrentAdminImpersonationSession(): Promise<ImpersonationSession | null> {
  if (typeof window === 'undefined') return null

  try {
    console.log('🚨 SECURITY: getCurrentAdminImpersonationSession() should be called from React context with current user')

    // For now, fall back to the existing logic but with security warnings
    return getImpersonationSession()
  } catch (error) {
    console.error('🚨 SECURITY ERROR: Failed to get current admin ID:', error)
    return null
  }
}

/**
 * SECURE: Get impersonation session with explicit admin ID validation
 * This is the recommended way to get impersonation sessions in multi-admin environments
 */
export function getSecureImpersonationSession(currentAdminId: string): ImpersonationSession | null {
  if (typeof window === 'undefined') return null

  try {
    const sessionKey = getImpersonationSessionKey(currentAdminId)
    const sessionData = sessionStorage.getItem(sessionKey)

    if (!sessionData) return null

    const session: ImpersonationSession = JSON.parse(sessionData)

    // Validate that the session belongs to the current admin
    if (session.adminId !== currentAdminId) {
      console.error('🚨 IMPERSONATION SECURITY: Session admin ID mismatch — clearing', { sessionAdminId: session.adminId, currentAdminId })
      clearImpersonationSession(currentAdminId)
      return null
    }

    // Check if session has expired
    const now = new Date()
    const lastActivity = new Date(session.lastActivity)
    const timeDiff = now.getTime() - lastActivity.getTime()

    if (timeDiff > SESSION_TIMEOUT) {
      clearImpersonationSession(currentAdminId)
      return null
    }

    // Update last activity
    session.lastActivity = now
    sessionStorage.setItem(sessionKey, JSON.stringify(session))

    return session
  } catch (error) {
    console.error('🚨 IMPERSONATION ERROR: Failed to get secure session:', error)
    clearImpersonationSession(currentAdminId)
    return null
  }
}

/**
 * Set impersonation session in session storage
 */
export function setImpersonationSession(session: ImpersonationSession): void {
  if (typeof window === 'undefined') return

  try {
    const sessionKey = getImpersonationSessionKey(session.adminId)
    sessionStorage.setItem(sessionKey, JSON.stringify(session))
    console.log('🎭 IMPERSONATION: Session started for', session.impersonatedUser.name)

    // Clear any generic session to prevent conflicts
    const genericKey = getImpersonationSessionKey()
    if (sessionStorage.getItem(genericKey)) {
      sessionStorage.removeItem(genericKey)
    }

    // Dispatch event to notify components that impersonation has changed
    window.dispatchEvent(new CustomEvent('impersonationChanged'))
  } catch (error) {
    console.error('🎭 IMPERSONATION ERROR: Failed to set session:', error)
  }
}

/**
 * Clear impersonation session from session storage
 */
export function clearImpersonationSession(adminId?: string): void {
  if (typeof window === 'undefined') return

  try {
    let wasCleared = false

    // Clear admin-specific session if adminId provided
    if (adminId) {
      const adminSessionKey = getImpersonationSessionKey(adminId)
      if (sessionStorage.getItem(adminSessionKey)) {
        sessionStorage.removeItem(adminSessionKey)
        wasCleared = true
      }
    }

    // Also clear generic session for backward compatibility
    const genericKey = getImpersonationSessionKey()
    if (sessionStorage.getItem(genericKey)) {
      sessionStorage.removeItem(genericKey)
      wasCleared = true
    }

    if (wasCleared) {
      console.log('🎭 IMPERSONATION: Session cleared')
      // Only dispatch if something was actually removed — prevents spurious
      // impersonationChanged events from triggering unnecessary profile refetches.
      window.dispatchEvent(new CustomEvent('impersonationChanged'))
    }
  } catch (error) {
    console.error('🎭 IMPERSONATION ERROR: Failed to clear session:', error)
  }
}

/**
 * Start impersonating a user
 */
export async function startImpersonation(
  adminId: string,
  targetUser: ImpersonatedUser
): Promise<{ success: boolean; error?: string }> {
  try {
    const response = await fetch('/api/admin/impersonation/start', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        targetUserId: targetUser.id,
      }),
    })

    if (!response.ok) {
      const error = await response.text()
      return { success: false, error }
    }

    const session: ImpersonationSession = {
      adminId,
      impersonatedUser: targetUser,
      startedAt: new Date(),
      lastActivity: new Date(),
    }

    setImpersonationSession(session)

    return { success: true }
  } catch (error) {
    console.error('Error starting impersonation:', error)
    return { success: false, error: 'Failed to start impersonation' }
  }
}

/**
 * Stop impersonating a user
 */
export async function stopImpersonation(adminId?: string): Promise<{ success: boolean; error?: string }> {
  try {
    // Try to get session with admin ID if provided, otherwise use fallback logic
    const session = adminId ? getSecureImpersonationSession(adminId) : getImpersonationSession()
    if (!session) {
      console.log('🎭 IMPERSONATION: No active session found, already stopped')
      return { success: true } // Already not impersonating
    }

    console.log('🎭 IMPERSONATION: Stopping session for', session.impersonatedUser.name, 'admin:', session.adminId)

    const response = await fetch('/api/admin/impersonation/stop', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        targetUserId: session.impersonatedUser.id,
      }),
    })

    if (!response.ok) {
      const error = await response.text()
      console.error('🎭 IMPERSONATION ERROR: Stop failed:', error)
      return { success: false, error }
    }

    // CRITICAL: Clear the admin-specific session using the session's admin ID
    clearImpersonationSession(session.adminId)
    console.log('🎭 IMPERSONATION: Session stopped successfully for admin:', session.adminId)

    return { success: true }
  } catch (error) {
    console.error('🎭 IMPERSONATION ERROR: Failed to stop impersonation:', error)
    return { success: false, error: 'Failed to stop impersonation' }
  }
}

/**
 * Check if currently impersonating a user
 */
export function isImpersonating(): boolean {
  return getImpersonationSession() !== null
}

/**
 * Get the currently impersonated user
 */
export function getImpersonatedUser(): ImpersonatedUser | null {
  const session = getImpersonationSession()
  return session?.impersonatedUser || null
}

/**
 * Validate that the current user is an admin or super admin
 */
export function validateAdminAccess(userRole: UserRole): boolean {
  return userRole === 'admin' || userRole === 'super_admin'
}

/**
 * Get impersonation status for display
 */
export function getImpersonationStatus(): {
  isActive: boolean
  user?: ImpersonatedUser
  duration?: string
} {
  const session = getImpersonationSession()

  if (!session) {
    return { isActive: false }
  }

  const startTime = new Date(session.startedAt)
  const now = new Date()
  const durationMs = now.getTime() - startTime.getTime()

  // Format duration
  const hours = Math.floor(durationMs / (1000 * 60 * 60))
  const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60))

  let duration = ''
  if (hours > 0) {
    duration = `${hours}h ${minutes}m`
  } else {
    duration = `${minutes}m`
  }

  return {
    isActive: true,
    user: session.impersonatedUser,
    duration,
  }
}

/**
 * CRITICAL: Force clear all impersonation sessions (emergency cleanup)
 */
export function emergencyClearAllSessions(): void {
  if (typeof window === 'undefined') return

  try {
    console.log('🚨 EMERGENCY: Clearing all impersonation sessions')

    // Clear generic session key
    const genericKey = getImpersonationSessionKey()
    sessionStorage.removeItem(genericKey)
    localStorage.removeItem(genericKey) // Clear from localStorage too if it exists

    // Clear any other potential session keys
    const keysToRemove = []
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i)
      if (key && key.includes('impersonation')) {
        keysToRemove.push(key)
      }
    }

    keysToRemove.forEach(key => {
      sessionStorage.removeItem(key)
      console.log('🚨 EMERGENCY: Removed session key:', key)
    })

    console.log('🚨 EMERGENCY: All impersonation sessions cleared')
  } catch (error) {
    console.error('🚨 EMERGENCY: Failed to clear sessions:', error)
  }
}

// Module-level singleton — prevents stacking intervals when ImpersonationProvider remounts
let cleanupIntervalId: ReturnType<typeof setInterval> | null = null

/**
 * Auto-cleanup expired sessions on page load.
 * Safe to call multiple times — only one interval will ever run.
 */
export function initializeImpersonationCleanup(): void {
  if (typeof window === 'undefined') return

  // Clean up expired sessions immediately
  getImpersonationSession()

  // Only register one periodic cleanup interval, no matter how many times this is called
  if (cleanupIntervalId !== null) return
  cleanupIntervalId = setInterval(() => {
    getImpersonationSession()
  }, 5 * 60 * 1000) // Check every 5 minutes
}

/**
 * Helper function to validate session data structure and timestamps
 */
function validateSessionData(session: any): boolean {
  try {
    // Check for required fields
    if (!session.adminId || !session.impersonatedUser || !session.impersonatedUser.id) {
      console.log('🚨 SESSION CORRUPTION: Missing required fields, clearing session')
      clearImpersonationSession(session.adminId)
      return false
    }

    // Check for reasonable timestamps
    const now = new Date().getTime()
    const startedAt = new Date(session.startedAt).getTime()
    const lastActivity = new Date(session.lastActivity).getTime()

    if (isNaN(startedAt) || isNaN(lastActivity) || startedAt > now || lastActivity > now) {
      console.log('🚨 SESSION CORRUPTION: Invalid timestamps, clearing session')
      clearImpersonationSession(session.adminId)
      return false
    }

    console.log('✅ SESSION INTEGRITY: Session is valid')
    return true
  } catch (error) {
    console.error('🚨 SESSION CORRUPTION: Failed to validate session data:', error)
    clearImpersonationSession()
    return false
  }
}

/**
 * Validate session integrity and clear if corrupted
 */
export function validateSessionIntegrity(): boolean {
  if (typeof window === 'undefined') return true

  try {
    // Check both generic and any admin-specific sessions
    const genericKey = getImpersonationSessionKey()
    const sessionData = sessionStorage.getItem(genericKey)
    if (!sessionData) {
      // Also check for any admin-specific sessions
      const allKeys = Object.keys(sessionStorage)
      const impersonationKeys = allKeys.filter(key => key.startsWith('admin_impersonation_session_'))
      if (impersonationKeys.length === 0) return true

      // Validate the first admin-specific session found
      const firstAdminSession = sessionStorage.getItem(impersonationKeys[0])
      if (!firstAdminSession) return true

      const session = JSON.parse(firstAdminSession)
      return validateSessionData(session)
    }

    const session = JSON.parse(sessionData)
    return validateSessionData(session)
  } catch (error) {
    console.error('🚨 SESSION CORRUPTION: Failed to validate session, clearing:', error)
    clearImpersonationSession()
    return false
  }
}